"use client"

import { useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import {
	IconDatabaseOutline18 as Database,
	IconPencilOutline18 as Pencil,
	IconPlusOutline18 as Plus,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import {
	Alert,
	AlertDialog,
	Badge,
	Button,
	Dialog,
	Form,
	FormInput,
	FormSelect,
	FormTextarea,
	IntegrationTemplate,
	Loader,
	SubmitButton,
	Switch,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { IntegrationLayout } from "./integration-layout"

const PARAM_TYPES = ["string", "number", "boolean", "timestamp"] as const

const PARAM_SOURCES = [
	{ value: "agent", label: "The agent chooses" },
	{ value: "contact.email", label: "Caller's email" },
	{ value: "contact.name", label: "Caller's name" },
	{ value: "company.name", label: "Caller's company" },
	{ value: "company.domain", label: "Caller's company domain" },
] as const

const connectSchema = z.object({
	connectionString: z
		.string()
		.min(1, "Connection string is required")
		.refine(
			(s) => /^postgres(ql)?:\/\//i.test(s.trim()),
			"Must start with postgres:// or postgresql://",
		),
})

const whitelistParameterSchema = z.object({
	name: z
		.string()
		.min(1, "Required")
		.regex(/^[a-z][a-z0-9_]*$/i, "letters, digits and _ only"),
	type: z.enum(PARAM_TYPES),
	source: z.string().default("agent"),
})

const whitelistSchema = z.object({
	name: z
		.string()
		.min(1, "Required")
		.regex(/^[a-z][a-z0-9_]*$/, "lowercase letters, digits, _"),
	description: z.string().min(1, "Required"),
	sql: z.string().min(1, "Required"),
	parameters: z.array(whitelistParameterSchema),
	customerFacing: z.boolean().default(false),
})

type WhitelistFormValues = z.infer<typeof whitelistSchema>

export function PostgresIntegrationPage() {
	const statusQuery = api.postgresIntegration.status.useQuery()
	const queriesQuery = api.postgresIntegration.listWhitelistedQueries.useQuery()

	if (statusQuery.isLoading) {
		return (
			<div className="flex justify-center pt-10">
				<Loader />
			</div>
		)
	}

	const settings = statusQuery.data?.settings ?? null
	const queries = queriesQuery.data?.queries ?? []

	return (
		<>
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<Database className="h-8 w-8 text-primary" />}
				name={
					<div className="flex flex-1 items-center gap-3 font-medium">
						PostgreSQL
						{settings ? <Badge variant="green">Connected</Badge> : null}
					</div>
				}
				description="Give the sidebar AI assistant read access to a PostgreSQL database. The AI runs every query in a read-only transaction; whitelisted parameterized queries run automatically, everything else needs your approval in chat."
				button={null}
				instructions={
					<div className="relative flex flex-col gap-8">
						{settings ? (
							<ConnectedView
								settings={settings}
								onDisconnect={async () => {
									await statusQuery.refetch()
									await queriesQuery.refetch()
								}}
							/>
						) : (
							<ConnectForm
								onConnected={async () => {
									await statusQuery.refetch()
								}}
							/>
						)}

						{settings && (
							<AutoRunSection
								enabled={settings.autoRunQueries ?? false}
								onChanged={() => statusQuery.refetch()}
							/>
						)}

						{settings && (
							<WhitelistSection
								queries={queries}
								isLoading={queriesQuery.isLoading}
								onChanged={() => queriesQuery.refetch()}
							/>
						)}
					</div>
				}
			/>
		</>
	)
}

function ConnectForm({ onConnected }: { onConnected: () => Promise<void> }) {
	const [testResult, setTestResult] = useState<{
		serverVersion: string | null
		database: string | null
		tableCount: number
	} | null>(null)
	const testMutation = api.postgresIntegration.test.useMutation({
		onSuccess: (data) => {
			setTestResult({
				serverVersion: data.serverVersion ?? null,
				database: data.database ?? null,
				tableCount: data.tableCount,
			})
			toast.success({ title: "Connected to database" })
		},
		onError: (error) => {
			setTestResult(null)
			toast.error({ title: "Connection failed", description: error.message })
		},
	})
	const connectMutation = api.postgresIntegration.connect.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Saved" })
			await onConnected()
		},
		onError: (error) => {
			toast.error({ title: "Failed to save", description: error.message })
		},
	})
	const form = useZodForm({ schema: connectSchema })

	return (
		<Form
			form={form}
			onSubmit={(values) => {
				connectMutation.mutate({ connectionString: values.connectionString })
			}}
			className="flex flex-col gap-3"
		>
			<div className="text-md text-secondary">
				Paste a connection string. We will encrypt it at rest using AES-256-GCM
				and only decrypt it server-side at query time. Use a read-only role for
				best results. Every query runs inside a READ ONLY transaction
				regardless.
			</div>
			<FormInput
				name="connectionString"
				placeholder="postgresql://user:password@host:5432/database"
				type="password"
				allowPasswordManager={false}
			/>
			<div className="flex items-center gap-3">
				<Button
					variant="outline"
					type="button"
					isLoading={testMutation.isLoading}
					onClick={() => {
						const value = form.getValues("connectionString")
						const parsed = connectSchema.safeParse({ connectionString: value })
						if (!parsed.success) {
							toast.error({
								title: "Connection string is required",
							})
							return
						}
						testMutation.mutate({ connectionString: value })
					}}
				>
					Test connection
				</Button>
				<SubmitButton isLoading={connectMutation.isLoading}>Save</SubmitButton>
			</div>
			{testResult && (
				<div className="rounded-md border bg-gray-50 p-3 text-xs text-secondary dark:bg-gray-900">
					<div>Database: {testResult.database ?? "unknown"}</div>
					<div>Tables (public schema): {testResult.tableCount}</div>
					{testResult.serverVersion && (
						<div className="mt-1 truncate">{testResult.serverVersion}</div>
					)}
				</div>
			)}
		</Form>
	)
}

interface ConnectedSettings {
	id: string
	host?: string | null
	database?: string | null
	serverVersion?: string | null
	lastTestedAt?: Date | null
	lastTestStatus?: string | null
	autoRunQueries?: boolean
}

function ConnectedView({
	settings,
	onDisconnect,
}: {
	settings: ConnectedSettings
	onDisconnect: () => Promise<void>
}) {
	const [open, setOpen] = useState(false)
	const disconnectMutation = api.postgresIntegration.disconnect.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Disconnected" })
			await onDisconnect()
		},
	})
	const refreshMutation = api.postgresIntegration.refreshTest.useMutation({
		onSuccess: () => toast.success({ title: "Connection healthy" }),
		onError: (error) =>
			toast.error({ title: "Connection failed", description: error.message }),
	})

	return (
		<div className="flex flex-col gap-3">
			<div className="rounded-md border bg-gray-50 p-3 text-xs text-secondary dark:bg-gray-900">
				<div>Database: {settings.database ?? "unknown"}</div>
				{settings.host && <div>Host: {settings.host}</div>}
				{settings.serverVersion && (
					<div className="mt-1 truncate">{settings.serverVersion}</div>
				)}
				{settings.lastTestedAt && (
					<div className="mt-1 text-tertiary">
						Last tested: {new Date(settings.lastTestedAt).toLocaleString()} ·{" "}
						{settings.lastTestStatus ?? "ok"}
					</div>
				)}
			</div>
			<div className="flex items-center gap-2">
				<Button
					variant="outline"
					isLoading={refreshMutation.isLoading}
					onClick={() => refreshMutation.mutate()}
				>
					Test connection
				</Button>
				<Button onClick={() => setOpen(true)}>Disconnect</Button>
			</div>
			<AlertDialog
				variant="positive"
				isOpen={open}
				onOpenChange={setOpen}
				confirmText="Disconnect"
				title="Disconnect PostgreSQL?"
				description="The encrypted connection string and all whitelisted queries will be removed. You can reconnect at any time."
				onCancel={() => setOpen(false)}
				onConfirm={() => {
					disconnectMutation.mutate()
					setOpen(false)
				}}
			/>
		</div>
	)
}

function AutoRunSection({
	enabled,
	onChanged,
}: {
	enabled: boolean
	onChanged: () => Promise<unknown> | void
}) {
	const [confirmOpen, setConfirmOpen] = useState(false)
	const setAutoRun = api.postgresIntegration.setAutoRun.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Saved" })
			await onChanged()
		},
		onError: (error) =>
			toast.error({ title: "Failed to save", description: error.message }),
	})

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-col gap-1">
				<span className="text-[10px] font-medium tracking-[0.08em] text-tertiary">
					Danger zone
				</span>
				<h3 className="text-base font-semibold leading-tight text-primary">
					Unsafely allow all queries
				</h3>
			</div>
			<Alert
				variant="warning"
				header="The assistant runs every query it writes, with no approval step"
			>
				Turn this on and the sidebar AI runs any query it generates immediately.
				Queries still run in a read-only transaction, so writes stay blocked,
				but the assistant can read any data this connection can reach without
				you reviewing the SQL first. Connect with a least-privilege, read-only
				role.
			</Alert>
			<Switch
				name="postgres-auto-run"
				variant="important"
				direction="switch-first"
				label="Auto-run queries the assistant generates"
				description="When off, every non-whitelisted query needs your approval in chat."
				checked={enabled}
				disabled={setAutoRun.isLoading}
				onCheckedChange={(next) => {
					if (next) {
						setConfirmOpen(true)
					} else {
						setAutoRun.mutate({ enabled: false })
					}
				}}
			/>
			<AlertDialog
				variant="positive"
				isOpen={confirmOpen}
				onOpenChange={setConfirmOpen}
				confirmText="Enable auto-run"
				title="Allow the assistant to run queries automatically?"
				description="The sidebar AI will run any query it generates immediately, with no approval step. Queries stay read-only, but it can read any data this connection can reach. You can turn this off at any time."
				onCancel={() => setConfirmOpen(false)}
				onConfirm={() => {
					setAutoRun.mutate({ enabled: true })
					setConfirmOpen(false)
				}}
			/>
		</div>
	)
}

interface WhitelistedQueryRow {
	id: string
	name: string
	description: string
	sql: string
	parameters: unknown
	customerFacing?: boolean
}

// Custom easings. The built-in CSS easings feel weak; these have the punch
// Emil's pieces use for "snappy but not jittery" entrances.
const EASE_OUT = [0.16, 1, 0.3, 1] as const
const EASE_OUT_QUICK = [0.32, 0.72, 0, 1] as const

function WhitelistSection({
	queries,
	isLoading,
	onChanged,
}: {
	queries: WhitelistedQueryRow[]
	isLoading: boolean
	onChanged: () => void
}) {
	const [editing, setEditing] = useState<WhitelistedQueryRow | null>(null)
	const [creating, setCreating] = useState(false)
	const [deleteId, setDeleteId] = useState<string | null>(null)
	const reduce = useReducedMotion()

	const removeMutation =
		api.postgresIntegration.removeWhitelistedQuery.useMutation({
			onSuccess: () => {
				toast.success({ title: "Query removed" })
				onChanged()
			},
		})

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-end justify-between gap-4">
				<div className="flex flex-col gap-1">
					<span className="text-[10px] font-medium tracking-[0.08em] text-tertiary">
						Auto-run
					</span>
					<h3 className="text-base font-semibold leading-tight text-primary">
						Whitelisted queries
					</h3>
					<p className="max-w-xl text-[13px] leading-relaxed text-secondary">
						The AI runs these named, parameterized queries automatically. Values
						bind positionally, so they cannot become SQL.
					</p>
				</div>
				<Button
					size="default"
					Icon={Plus}
					onClick={() => setCreating(true)}
					className=""
				>
					Add query
				</Button>
			</div>

			{queries.length === 0 && !isLoading ? (
				<EmptyWhitelist onAdd={() => setCreating(true)} />
			) : (
				<motion.ul
					className="flex flex-col gap-1.5"
					initial={reduce ? false : "hidden"}
					animate="visible"
					variants={{
						hidden: {},
						visible: { transition: { staggerChildren: 0.04 } },
					}}
				>
					<AnimatePresence initial={false}>
						{queries.map((q) => (
							<QueryRow
								key={q.id}
								q={q}
								onEdit={() => setEditing(q)}
								onDelete={() => setDeleteId(q.id)}
							/>
						))}
					</AnimatePresence>
				</motion.ul>
			)}

			{creating && (
				<WhitelistEditor
					initial={null}
					onClose={() => setCreating(false)}
					onSaved={() => {
						setCreating(false)
						onChanged()
					}}
				/>
			)}
			{editing && (
				<WhitelistEditor
					initial={editing}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setEditing(null)
						onChanged()
					}}
				/>
			)}
			<AlertDialog
				variant="positive"
				isOpen={deleteId !== null}
				onOpenChange={(o) => !o && setDeleteId(null)}
				confirmText="Remove"
				title="Remove whitelisted query?"
				description="The AI will need approval to run this query in the future."
				onCancel={() => setDeleteId(null)}
				onConfirm={() => {
					if (deleteId) removeMutation.mutate({ id: deleteId })
					setDeleteId(null)
				}}
			/>
		</div>
	)
}

interface QueryParam {
	name: string
	type: (typeof PARAM_TYPES)[number]
}

const PARAM_TYPE_TINT: Record<(typeof PARAM_TYPES)[number], string> = {
	string: "text-blue-600 dark:text-blue-400",
	number: "text-amber-600 dark:text-amber-400",
	boolean: "text-emerald-600 dark:text-emerald-400",
	timestamp: "text-violet-600 dark:text-violet-400",
}

function QueryRow({
	q,
	onEdit,
	onDelete,
}: {
	q: WhitelistedQueryRow
	onEdit: () => void
	onDelete: () => void
}) {
	const params = (
		Array.isArray(q.parameters) ? q.parameters : []
	) as QueryParam[]

	return (
		<motion.li
			layout
			variants={{
				hidden: { opacity: 0, y: 6 },
				visible: { opacity: 1, y: 0 },
			}}
			exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
			transition={{ duration: 0.32, ease: EASE_OUT }}
			// `group` enables hover-only reveal of the action buttons on
			// pointer-fine devices; on touch they're always visible (see media
			// query below).
			className="group/row relative overflow-hidden rounded-lg border border-gray-200 bg-white transition-[background-color,border-color] duration-150 ease-out hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
		>
			<div className="flex items-start gap-3 px-3 py-2.5">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[12.5px] font-medium tabular-nums text-primary dark:bg-gray-800/60">
							{q.name}
						</span>
						{params.length > 0 && (
							<div className="flex items-center gap-1">
								{params.map((p) => (
									<span
										key={p.name}
										className={`text-[10.5px] font-medium uppercase tracking-[0.04em] ${PARAM_TYPE_TINT[p.type] ?? ""}`}
										title={`${p.name}: ${p.type}`}
									>
										{p.type[0]}
									</span>
								))}
							</div>
						)}
					</div>
					{q.description && (
						<p className="mt-1 truncate text-[12.5px] text-secondary">
							{q.description}
						</p>
					)}
					<p
						className="mt-1.5 truncate font-mono text-[11px] leading-relaxed text-tertiary"
						title={q.sql}
					>
						{q.sql.replace(/\s+/g, " ")}
					</p>
				</div>

				{/* Action cluster. Reveals on row hover for fine pointers, always
				    visible on touch since there's no hover state to discover them. */}
				<div className="flex shrink-0 items-center gap-0.5 opacity-100 transition-opacity duration-150 ease-out [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover/row:opacity-100">
					<button
						type="button"
						onClick={onEdit}
						aria-label="Edit query"
						className="flex h-7 w-7 items-center justify-center rounded-md text-tertiary transition-[color,background-color,transform] duration-150 ease-out hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800"
					>
						<Pencil strokeWidth={1.75} />
					</button>
					<button
						type="button"
						onClick={onDelete}
						aria-label="Delete query"
						className="flex h-7 w-7 items-center justify-center rounded-md text-tertiary transition-[color,background-color,transform] duration-150 ease-out hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
					>
						<Trash2 strokeWidth={1.75} />
					</button>
				</div>
			</div>
		</motion.li>
	)
}

function EmptyWhitelist({ onAdd }: { onAdd: () => void }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 4 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.32, ease: EASE_OUT_QUICK }}
			className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-25 px-6 py-10 text-center dark:border-gray-800 dark:bg-gray-900/40"
		>
			<div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-tertiary dark:bg-gray-800/60">
				<Database strokeWidth={1.75} />
			</div>
			<div className="flex flex-col gap-0.5">
				<p className="text-[13px] font-medium text-primary">
					No whitelisted queries yet
				</p>
				<p className="max-w-sm text-[12px] leading-relaxed text-tertiary">
					Without a whitelist every query the assistant proposes will need your
					approval in chat.
				</p>
			</div>
			<button
				type="button"
				onClick={onAdd}
				className="mt-1 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-medium text-secondary transition-[color,transform] duration-150 ease-out hover:text-primary"
			>
				<Plus strokeWidth={2} />
				Add your first query
			</button>
		</motion.div>
	)
}

function WhitelistEditor({
	initial,
	onClose,
	onSaved,
}: {
	initial: WhitelistedQueryRow | null
	onClose: () => void
	onSaved: () => void
}) {
	const initialParams = (() => {
		if (!initial || !Array.isArray(initial.parameters)) return []
		return (
			initial.parameters as { name: string; type: string; source?: string }[]
		).map((p) => ({
			name: p.name,
			type: PARAM_TYPES.includes(p.type as (typeof PARAM_TYPES)[number])
				? (p.type as (typeof PARAM_TYPES)[number])
				: "string",
			source: p.source ?? "agent",
		}))
	})()

	const form = useZodForm({
		schema: whitelistSchema,
		defaultValues: {
			name: initial?.name ?? "",
			description: initial?.description ?? "",
			sql: initial?.sql ?? "",
			parameters: initialParams,
			customerFacing: initial?.customerFacing ?? false,
		},
	})
	const params =
		(form.watch("parameters" as never) as WhitelistFormValues["parameters"]) ??
		[]

	const addMutation = api.postgresIntegration.addWhitelistedQuery.useMutation({
		onSuccess: () => {
			toast.success({ title: "Query saved" })
			onSaved()
		},
		onError: (error) =>
			toast.error({ title: "Failed to save", description: error.message }),
	})
	const updateMutation =
		api.postgresIntegration.updateWhitelistedQuery.useMutation({
			onSuccess: () => {
				toast.success({ title: "Query saved" })
				onSaved()
			},
			onError: (error) =>
				toast.error({ title: "Failed to save", description: error.message }),
		})

	const isSaving = addMutation.isLoading || updateMutation.isLoading

	return (
		<Dialog
			isOpen
			onOpenChange={(o) => !o && onClose()}
			title={initial ? "Edit whitelisted query" : "Add whitelisted query"}
		>
			<Form
				form={form}
				onSubmit={(rawValues) => {
					const values = rawValues as unknown as WhitelistFormValues
					if (initial) {
						updateMutation.mutate({ id: initial.id, ...values })
					} else {
						addMutation.mutate(values)
					}
				}}
				className="flex flex-col gap-3 px-1 pb-1"
			>
				<div>
					<div className="mb-1 text-xs font-medium text-secondary">Name</div>
					<FormInput
						name="name"
						placeholder="recent_users"
						disabled={!!initial}
					/>
				</div>
				<div>
					<div className="mb-1 text-xs font-medium text-secondary">
						Description
					</div>
					<FormInput
						name="description"
						placeholder="Users created since a date"
					/>
				</div>
				<div>
					<div className="mb-1 text-xs font-medium text-secondary">
						SQL (parameterize with $1, $2, …)
					</div>
					<FormTextarea
						name="sql"
						rows={6}
						className="font-mono text-xs"
						placeholder='SELECT id, email FROM "User" WHERE "createdAt" > $1 LIMIT 100'
					/>
				</div>
				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<span className="text-xs font-medium text-secondary">
							Parameters (positional, in order of $1, $2, …)
						</span>
						<Button
							size="sm"
							variant="outline"
							type="button"
							onClick={() =>
								form.setValue(
									"parameters" as never,
									[
										...params,
										{
											name: `param${params.length + 1}`,
											type: "string",
											source: "agent",
										},
									] as never,
								)
							}
						>
							Add parameter
						</Button>
					</div>
					{params.length === 0 ? (
						<div className="text-xs text-tertiary">
							No parameters. The SQL must not reference $N.
						</div>
					) : (
						<div className="flex flex-col gap-2">
							{params.map((_, i) => (
								<div key={i} className="flex items-center gap-2">
									<div className="w-7 font-mono text-xs text-tertiary">
										${i + 1}
									</div>
									<FormInput
										name={`parameters.${i}.name`}
										placeholder="since"
										className="flex-1"
									/>
									<FormSelect
										name={`parameters.${i}.type`}
										options={PARAM_TYPES.map((t) => ({
											value: t,
											label: t,
										}))}
									/>
									<FormSelect
										name={`parameters.${i}.source`}
										options={PARAM_SOURCES.map((source) => ({
											value: source.value,
											label: source.label,
										}))}
									/>
									<Button
										size="sm"
										variant="outline"
										type="button"
										Icon={Trash2}
										onClick={() => {
											const next = [...params]
											next.splice(i, 1)
											form.setValue("parameters" as never, next as never)
										}}
									/>
								</div>
							))}
						</div>
					)}
				</div>
				<div className="mt-4">
					<Switch
						name="customer-facing"
						variant="important"
						direction="switch-first"
						label="Answer customers with this"
						description="The agent may run it for a verified customer, and reads back only the columns this query selects. Bind a parameter to the caller first, so it can only return their own row."
						checked={
							(form.watch("customerFacing" as never) as unknown as
								| boolean
								| undefined) ?? false
						}
						onCheckedChange={(next) =>
							form.setValue("customerFacing" as never, next as never)
						}
					/>
				</div>
				<div className="mt-2 flex items-center justify-end gap-2">
					<Button variant="outline" type="button" onClick={onClose}>
						Cancel
					</Button>
					<SubmitButton isLoading={isSaving}>Save</SubmitButton>
				</div>
			</Form>
		</Dialog>
	)
}
